import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { MapPin, Phone, ChevronRight } from "lucide-react";
import { COUNTIES, LOCATIONS_BY_COUNTY } from "../data/locations";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_tank-replace-pro/artifacts/isbxf87e_Logo%20Kool%20Plumbing-01.png";
const SITE_URL = "https://koolplumbing.com";

const CountyBlock = ({ countyKey }) => {
  const county = COUNTIES[countyKey];
  const cities = LOCATIONS_BY_COUNTY[countyKey];
  return (
    <div className="space-y-6" data-testid={`county-block-${countyKey}`}>
      <div className="flex items-center gap-3">
        <span className="w-10 h-10 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center"><MapPin size={20} /></span>
        <h2 className="text-2xl sm:text-3xl font-heading text-blue-900">Service areas in {county.name}</h2>
      </div>
      <p className="text-gray-500 text-sm max-w-3xl">{county.soilNote}</p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {cities.map((l) => (
          <Link key={l.slug} to={`/service-areas/${l.slug}`} data-testid={`area-link-${l.slug}`} className="group flex items-center justify-between px-5 py-4 bg-white border border-gray-200 rounded-xl hover:border-amber-400 hover:shadow-md transition">
            <span className="font-semibold text-blue-950 text-sm">{l.city}</span>
            <ChevronRight size={16} className="text-gray-300 group-hover:text-amber-600 group-hover:translate-x-0.5 transition" />
          </Link>
        ))}
      </div>
    </div>
  );
};

const ServiceAreasPage = () => {
  return (
    <div className="flex-1 font-body bg-gray-50" data-testid="service-areas-page">
      <Helmet>
        <title>Service Areas | Plumbing & Septic in Miami-Dade & Broward | Kool Plumbing</title>
        <meta name="description" content="Kool Plumbing provides plumbing and septic tank replacement across Miami-Dade and Broward County, FL — including Miami, Coral Gables, Fort Lauderdale, Weston, Pembroke Pines and more. Call (786) 622-4963." />
        <link rel="canonical" href={`${SITE_URL}/service-areas`} />
        <meta property="og:title" content="Service Areas | Kool Plumbing" />
        <meta property="og:type" content="website" />
      </Helmet>

      <section className="relative py-16 bg-cover bg-center" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1759355787118-7b48305965d8?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600')" }}>
        <div className="absolute inset-0 bg-slate-900/75"></div>
        <div className="relative max-w-5xl mx-auto px-6 text-white space-y-4">
          <nav className="text-xs text-blue-200 flex items-center gap-2">
            <Link to="/" className="hover:text-amber-400">Home</Link><ChevronRight size={12} /><span className="text-amber-400">Service Areas</span>
          </nav>
          <h1 className="text-4xl sm:text-5xl font-heading leading-tight tracking-tight">Where We Work</h1>
          <p className="text-lg text-gray-200 max-w-2xl">Kool Plumbing proudly serves homeowners across Miami-Dade and Broward County with expert plumbing and septic tank replacement. Find your city below.</p>
          <a href="tel:+17866224963" className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-amber-600 hover:bg-amber-700 text-white font-bold transition"><Phone size={18} /> (786) 622-4963</a>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-6xl mx-auto px-6 space-y-16">
          <CountyBlock countyKey="miamidade" />
          <CountyBlock countyKey="broward" />
        </div>
      </section>

      <footer className="bg-blue-950 text-white py-10">
        <div className="max-w-5xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <img src={LOGO_URL} alt="Kool Plumbing" className="h-12 w-auto" />
          <div className="text-xs text-gray-300 text-center sm:text-right space-y-1">
            <p className="flex items-center gap-2 justify-center sm:justify-end"><Phone size={13} className="text-amber-500" /> (786) 622-4963 · info@koolplumbing.com</p>
            <p>Serving Miami-Dade &amp; Broward · License #CFC1432999</p>
            <Link to="/" className="text-amber-500 hover:underline">← Back to homepage</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ServiceAreasPage;
